<template>
  <div>
    <v-row>
      <v-col cols="12" class="pt-0">
        <div class="align-center d-flex mt-1">
          <h2 class="font-weight-medium ma-0 text-h6">Corrigir erro</h2>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn aria-label="Como justificar o erro" class="ml-1 mt-1" color="primary" icon small v-bind="attrs" v-on="on">
                <v-icon>mdi-help-circle-outline</v-icon>
              </v-btn>
            </template>
            <span class="d-inline-block" style="max-width: 340px">
              Registre os ajustes realizados na BDGD e selecione todos os registros que devem ser vinculados a esta correção.
            </span>
          </v-tooltip>
          <v-spacer />
          <v-btn
            v-if="erro.encaminhamento_inicial === 'CORRIGIR_PRIMEIRO'"
            color="primary"
            outlined
            small
            to="/geracao-bdgd/atualizacoes-bdgd/historico"
          >
            <v-icon class="mr-1" small>mdi-open-in-new</v-icon>
            Ajustes da BDGD
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="pb-0">
        <v-text-field
          :value="resumoAjustesSelecionados"
          append-icon="mdi-magnify"
          label="Ajustes realizados"
          outlined
          readonly
          @click="dialogAjustes = true"
          @click:append="dialogAjustes = true"
        />
      </v-col>
    </v-row>

    <v-dialog v-model="dialogAjustes" max-width="900" scrollable>
      <v-card tile>
        <v-card-title class="body-1 font-weight-medium py-4">Selecionar ajustes realizados</v-card-title>
        <v-divider />
        <v-card-text class="pa-6" style="max-height: calc(100vh - 220px)">
          <v-select
            v-model="tipoAjusteSelecionado"
            :items="tiposAjuste"
            item-text="text"
            item-value="value"
            label="Tipo de ajuste realizado"
            outlined
          />

          <template v-if="tipoAjusteSelecionado">
            <div class="align-center d-flex mb-3">
              <span class="body-1 font-weight-medium">Ajustes disponíveis — {{ rotuloTipoAjuste(tipoAjusteSelecionado) }}</span>
              <v-spacer />
              <v-progress-circular
                v-if="carregandoRegistros[tipoAjusteSelecionado]"
                color="primary"
                indeterminate
                size="20"
                width="2"
              />
            </div>

            <v-text-field
              v-model="buscasPorTipo[tipoAjusteSelecionado]"
              append-icon="mdi-magnify"
              class="mb-4 px-5"
              hide-details
              label="Filtrar registros"
            />

            <v-data-table
              v-model="selecionadosPorTipo[tipoAjusteSelecionado]"
              :headers="cabecalhosAjustesDisponiveisPorTipo"
              :items="registrosPorTipo[tipoAjusteSelecionado] || []"
              :loading="carregandoRegistros[tipoAjusteSelecionado]"
              :search="buscasPorTipo[tipoAjusteSelecionado]"
              :items-per-page="-1"
              item-key="chave"
              no-data-text="Nenhum registro de ajuste disponível para este tipo."
              show-select
            >
              <template v-slot:[`item.id`]="{ item }">{{ item.id | addLeadingZero }}</template>
              <template v-slot:[`item.arquivo`]="{ item }">{{ item.arquivo }} ({{ item.entidade }})</template>
              <template v-slot:[`item.data_registro`]="{ item }">
                {{ item.data_registro | formatToMonth }} v{{ item.versao }}
              </template>
              <template v-slot:[`item.numero_linhas`]="{ item }">
                {{ item.numero_linhas | parseNumberToIntegerBR }}
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <general-status :status="item.status" />
              </template>
              <template v-slot:[`item.script_modelo_nome`]="{ item }">
                <span :title="item.script_modelo_descricao">{{ item.script_modelo_nome || '—' }}</span>
              </template>
              <template v-slot:[`item.cod_id`]="{ item }">
                {{ item.cod_id || '—' }}
              </template>
              <template v-slot:[`item.entidade_exibicao`]="{ item }">
                <span :class="classeAcaoAjusteEmTela(item.acao)">{{ item.entidade_exibicao }}</span>
              </template>
              <template v-slot:[`item.data_ajuste`]="{ item }">
                {{ item.data_ajuste | parseToDateTimeBR }}
              </template>
              <template v-slot:[`item.detalhe`]="{ item }">
                {{ item.detalhe || '—' }}
              </template>
            </v-data-table>
          </template>

          <div class="body-2 font-weight-medium mt-6">Ajustes selecionados ({{ ajustesSelecionados.length }})</div>
          <ajustes-selecionados-agrupados
            v-if="ajustesSelecionados.length"
            class="mt-2"
            :ajustes="ajustesSelecionados"
            permitir-remocao
            @remover="removerAjuste"
          />
          <div v-else class="ajustes-selecionados__vazio align-center d-flex grey--text justify-center">
            <v-icon class="mr-2" color="grey" small>mdi-information-outline</v-icon>
            Nenhum ajuste selecionado.
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn color="primary" @click="dialogAjustes = false">Concluir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row>
      <v-col cols="12" class="mb-2 py-0">
        <div v-if="ajustesSelecionados.length" class="body-2 font-weight-medium mb-2">
          Ajustes selecionados ({{ ajustesSelecionados.length }})
        </div>
        <ajustes-selecionados-agrupados
          v-if="ajustesSelecionados.length"
          :ajustes="ajustesSelecionados"
          permitir-remocao
          @remover="removerAjuste"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="mt-0 pt-0">
        <v-checkbox
          class="mb-1 mt-0 pt-0"
          :input-value="origemCorrigida"
          label="Corrigir origem"
          @change="$emit('update:origem-corrigida', $event)"
        />
        <v-select
          v-if="origemCorrigida"
          :value="origemErro"
          :items="origensErro"
          :rules="[regraObrigatoria]"
          item-text="text"
          item-value="value"
          label="Origem do erro"
          outlined
          @input="$emit('update:origem-erro', $event)"
        />
        <observacao-tarefa v-model="observacao" :rows="5" />
      </v-col>
    </v-row>
    <v-alert v-if="erroExecucao" class="mb-4" dense outlined type="error">{{ erroExecucao }}</v-alert>
    <div class="d-flex justify-end mt-8">
      <v-btn
        color="primary"
        :disabled="!ajustesSelecionados.length || (origemCorrigida && !origemErro)"
        :loading="executando"
        @click="finalizar"
      >
        Finalizar
      </v-btn>
    </div>
  </div>
</template>

<script>
  import GestaoBdgdsErrosTarefasService from '@/services/GestaoBdgdsErrosTarefasService.js';
  import { origensErro } from './origensErro.js';
  import AjustesSelecionadosAgrupados from './AjustesSelecionadosAgrupados.vue';
  import ObservacaoTarefa from './ObservacaoTarefa.vue';

  const tiposAjuste = [
    { text: 'Via Excel', value: 'VIA_EXCEL' },
    { text: 'Em massa', value: 'EM_MASSA' },
    { text: 'Script', value: 'SCRIPT' },
    { text: 'Em tela', value: 'EM_TELA' }
  ];

  const tabelasAjustesLegado = {
    ATUALIZACAO_BDGD: 'atualizacoes_bdgds',
    AJUSTE_EM_TELA_ENTIDADE: 'logs_ajustes_manuais_entidades_bdgds',
    AJUSTE_EM_TELA_TABELA: 'logs_ajustes_manuais_tabelas',
    ATUALIZACAO_VIA_SQL: 'atualizacoes_bdgds_via_sql',
    ATUALIZACAO_VIA_SCRIPT_MODELO: 'atualizacoes_bdgds_via_scripts_modelos'
  };

  const cabecalhosAjustesDisponiveis = [
    { text: '#', value: 'id' },
    { text: 'Descrição', value: 'descricao' }
  ];

  const cabecalhosAjustesViaExcel = [
    { text: '#', value: 'id' },
    { text: 'Empresa', value: 'empresa' },
    { text: 'Arquivo', value: 'arquivo' },
    { text: 'Mês dos dados', value: 'data_registro' },
    { text: 'Nº de linhas', value: 'numero_linhas' },
    { text: 'Status', value: 'status' }
  ];

  const cabecalhosAjustesEmTela = [
    { text: '#', value: 'gid' },
    { text: 'COD_ID', value: 'cod_id' },
    { text: 'Entidade', value: 'entidade_exibicao' },
    { text: 'Campo', value: 'campo' },
    { text: 'Ajustado por', value: 'nome_usuario' },
    { text: 'Ajustado em', value: 'data_ajuste' }
  ];

  const cabecalhosAjustesEmMassa = [
    { text: '#', value: 'id' },
    { text: 'Empresa', value: 'empresa' },
    { text: 'Título', value: 'titulo' },
    { text: 'Entidade', value: 'entidade_exibicao' },
    { text: 'Mês dos dados', value: 'data_registro' },
    { text: 'Nº de linhas', value: 'numero_linhas' },
    { text: 'Status', value: 'status' }
  ];

  const cabecalhosAjustesViaScript = [
    { text: '#', value: 'id' },
    { text: 'Empresa', value: 'empresa' },
    { text: 'Título', value: 'titulo' },
    { text: 'Script Modelo', value: 'script_modelo_nome' },
    { text: 'Mês dos dados', value: 'data_registro' },
    { text: 'Status', value: 'status' }
  ];

  export default {
    name: 'CorrigirErro',
    components: {
      AjustesSelecionadosAgrupados,
      GeneralStatus: () => import('@/components/general/GeneralStatus.vue'),
      ObservacaoTarefa
    },
    props: {
      erro: { type: Object, required: true },
      origemCorrigida: { type: Boolean, default: false },
      origemErro: { type: String, default: null }
    },
    data() {
      return {
        tiposAjuste,
        origensErro,
        cabecalhosAjustesDisponiveis,
        cabecalhosAjustesEmTela,
        cabecalhosAjustesEmMassa,
        cabecalhosAjustesViaExcel,
        cabecalhosAjustesViaScript,
        dialogAjustes: false,
        tipoAjusteSelecionado: 'VIA_EXCEL',
        registrosPorTipo: {},
        selecionadosPorTipo: {},
        buscasPorTipo: {},
        carregandoRegistros: {},
        observacao: '',
        executando: false,
        erroExecucao: ''
      };
    },
    computed: {
      ajustesSelecionados() {
        return this.tiposAjuste.reduce((registros, tipo) => registros.concat(this.selecionadosPorTipo[tipo.value] || []), []);
      },
      resumoAjustesSelecionados() {
        return this.ajustesSelecionados.length ? `${this.ajustesSelecionados.length} ajuste(s) selecionado(s)` : '';
      },
      cabecalhosAjustesDisponiveisPorTipo() {
        if (this.tipoAjusteSelecionado === 'VIA_EXCEL') return this.cabecalhosAjustesViaExcel;
        if (this.tipoAjusteSelecionado === 'EM_TELA') return this.cabecalhosAjustesEmTela;
        if (this.tipoAjusteSelecionado === 'EM_MASSA') return this.cabecalhosAjustesEmMassa;
        if (this.tipoAjusteSelecionado === 'SCRIPT') return this.cabecalhosAjustesViaScript;

        return this.cabecalhosAjustesDisponiveis;
      }
    },
    created() {
      this.prepararTipoAjuste(this.tipoAjusteSelecionado);
    },
    methods: {
      async finalizar() {
        if (this.executando || !this.ajustesSelecionados.length || (this.origemCorrigida && !this.origemErro)) return;
        this.executando = true;
        this.erroExecucao = '';
        try {
          const dados = {
            acao: 'CORRIGIR',
            origem_corrigida: this.origemCorrigida,
            observacao: this.observacao.trim() || null,
            ajustes: this.ajustesSelecionados.map((registro) => ({
              tabela_ajuste: registro.tabela_ajuste,
              registro_id: registro.id
            }))
          };

          if (this.origemCorrigida) dados.origem_erro = this.origemErro;

          await GestaoBdgdsErrosTarefasService.executarCorrigirPrimeiro(this.erro.id, dados);
          this.$emit('executada');
        } catch (error) {
          this.erroExecucao = error?.response?.data?.message || 'Não foi possível executar o fluxo.';
        } finally {
          this.executando = false;
        }
      },
      rotuloTipoAjuste(tipo) {
        return this.tiposAjuste.find((item) => item.value === tipo)?.text || tipo;
      },
      regraObrigatoria(valor) {
        return !!valor || 'Campo obrigatório.';
      },
      classeAcaoAjusteEmTela(acao) {
        return {
          'error--text': acao === 'DELETE',
          'success--text': acao === 'INSERT',
          'warning--text': acao === 'UPDATE'
        };
      },
      prepararTipoAjuste(tipo) {
        if (!tipo) return;

        if (!this.selecionadosPorTipo[tipo]) this.$set(this.selecionadosPorTipo, tipo, []);
        if (!this.buscasPorTipo[tipo]) this.$set(this.buscasPorTipo, tipo, '');
        this.carregarRegistros(tipo);
      },
      removerAjuste(ajuste) {
        const ajustesDoTipo = this.selecionadosPorTipo[ajuste.tipo_ajuste] || [];
        this.$set(
          this.selecionadosPorTipo,
          ajuste.tipo_ajuste,
          ajustesDoTipo.filter((item) => item.chave !== ajuste.chave)
        );
      },
      async carregarRegistros(tipo) {
        if (this.carregandoRegistros[tipo] || Object.prototype.hasOwnProperty.call(this.registrosPorTipo, tipo)) return;
        this.$set(this.carregandoRegistros, tipo, true);
        try {
          const { data } = await GestaoBdgdsErrosTarefasService.listarAjustesCorrigirPrimeiro(this.erro.id, [tipo]);
          this.$set(
            this.registrosPorTipo,
            tipo,
            ((data.data && data.data[tipo]) || []).map((registro) => {
              const tabelaAjuste = registro.tabela_ajuste || tabelasAjustesLegado[registro.origem];

              return {
                ...registro,
                tabela_ajuste: tabelaAjuste,
                chave: `${tipo}-${tabelaAjuste}-${registro.id}`,
                tipo_ajuste: tipo
              };
            })
          );
        } catch (error) {
          this.$toast.error('Não foi possível consultar os registros de ajustes.', '', { position: 'topRight' });
        } finally {
          this.$set(this.carregandoRegistros, tipo, false);
        }
      }
    },
    watch: {
      tipoAjusteSelecionado(tipo) {
        this.prepararTipoAjuste(tipo);
      }
    }
  };
</script>

<style scoped>
  .ajustes-selecionados__vazio {
    min-height: 68px;
  }
</style>
